import React from 'react';
import { motion } from 'motion/react';
import type { Variants } from 'motion/react';
import { Box } from '@mui/material';
import { stagger as staggerTokens } from '../theme/tokens';
import { IN_VIEW_MARGIN, staggerContainer, staggerItem } from './variants';
import { useMotionSafe } from './useMotionSafe';
import type { MotionBoxProps } from './types';

const MotionBox = motion.create(Box);

interface StaggerProps extends MotionBoxProps {
  children: React.ReactNode;
  /** Seconds between children. */
  interval?: number;
  /** Seconds before the first child. */
  delay?: number;
  /** Fire on scroll-into-view instead of on mount. */
  whenInView?: boolean;
}

/**
 * Reveals its children one after another. Wrap each child in `StaggerItem`.
 * Used for card grids, lists, and (with a tight interval) table rows.
 */
export const Stagger: React.FC<StaggerProps> = ({
  children,
  interval = staggerTokens.base,
  delay = 0,
  whenInView = false,
  ...boxProps
}) => {
  const { enabled } = useMotionSafe();

  if (!enabled) {
    return <Box {...boxProps}>{children}</Box>;
  }

  const trigger = whenInView
    ? ({ initial: 'hidden', whileInView: 'visible', viewport: { once: true, margin: IN_VIEW_MARGIN } } as const)
    : ({ initial: 'hidden', animate: 'visible' } as const);

  return (
    <MotionBox variants={staggerContainer(interval, delay)} {...trigger} {...boxProps}>
      {children}
    </MotionBox>
  );
};

interface StaggerItemProps extends MotionBoxProps {
  children: React.ReactNode;
  variants?: Variants;
}

export const StaggerItem: React.FC<StaggerItemProps> = ({
  children,
  variants = staggerItem,
  ...boxProps
}) => {
  const { enabled } = useMotionSafe();

  if (!enabled) {
    return <Box {...boxProps}>{children}</Box>;
  }

  return (
    <MotionBox variants={variants} {...boxProps}>
      {children}
    </MotionBox>
  );
};

export default Stagger;
